// src/Components/AdminSettingsPage.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiFetch from '../utils/api';

function AdminSettingsPage() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [envVars, setEnvVars] = useState({});
  const [form, setForm] = useState({
    gatewayUrl: '',
    gatewayApiKey: '',
    dashboardUrl: '',
    dashboardPublicUrl: '',
    botImage: '',
    defaultCpuRequest: '',
    defaultMemoryRequest: '',
    defaultCpuLimit: '',
    defaultMemoryLimit: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/admin/settings', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setForm(data.settings);
        setEnvVars(data.envVars || {});
      } else {
        throw new Error('Failed to fetch settings');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await apiFetch('/api/admin/settings', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (response.ok) {
        setSuccess('Settings saved successfully');
        fetchSettings();
      } else {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save settings');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const EnvBadge = ({ field }) => {
    const status = envVars[field];
    if (!status) return null;
    return (
      <Chip
        label={`ENV ${status}`}
        size="small"
        color={status === '(set)' ? 'success' : 'default'}
        variant="outlined"
        sx={{ ml: 1 }}
      />
    );
  };

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
              System Settings
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Configure gateway connections, dashboard URLs, and default bot resource limits.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={fetchSettings} startIcon={<RefreshIcon />}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Gateway Connection
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connection details for the Discord Gateway service that manages bot deployments in Kubernetes.
          Database values override environment variables.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle2">Gateway URL</Typography>
              <EnvBadge field="gatewayUrl" />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. http://gateway.highland-discord-gateway.svc.cluster.local"
              value={form.gatewayUrl}
              onChange={handleChange('gatewayUrl')}
              helperText="Internal Kubernetes service URL for the gateway API"
            />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle2">Gateway API Key</Typography>
              <EnvBadge field="gatewayApiKey" />
            </Box>
            <TextField
              fullWidth
              size="small"
              type="password"
              placeholder="Gateway API key"
              value={form.gatewayApiKey}
              onChange={handleChange('gatewayApiKey')}
              helperText="API key for authenticating with the gateway service"
            />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Dashboard URLs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          URLs injected into bot environment variables so bots can push logs and attachments to this dashboard.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle2">Dashboard URL (Internal)</Typography>
              <EnvBadge field="dashboardUrl" />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. http://dashboard-backend.highland-infra.svc.cluster.local:5000"
              value={form.dashboardUrl}
              onChange={handleChange('dashboardUrl')}
              helperText="Internal URL bots use to call the dashboard API (set as MM_DASHBOARD_URL)"
            />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle2">Dashboard URL (Public)</Typography>
              <EnvBadge field="dashboardPublicUrl" />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. https://dashboard.highland-networks.com"
              value={form.dashboardPublicUrl}
              onChange={handleChange('dashboardPublicUrl')}
              helperText="Public URL used for user-facing log and attachment links (set as MM_DASHBOARD_PUBLIC_URL)"
            />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Bot Defaults
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Default container image and resource limits for newly created bots.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle2">Bot Container Image</Typography>
              <EnvBadge field="botImage" />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. registry.example.com/modmailbot:latest"
              value={form.botImage}
              onChange={handleChange('botImage')}
              helperText="Default Docker image used when creating new bot deployments"
            />
          </Box>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">Resource Requests & Limits</Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="CPU Request"
              size="small"
              placeholder="e.g. 100m"
              value={form.defaultCpuRequest}
              onChange={handleChange('defaultCpuRequest')}
            />
            <TextField
              label="CPU Limit"
              size="small"
              placeholder="e.g. 500m"
              value={form.defaultCpuLimit}
              onChange={handleChange('defaultCpuLimit')}
            />
            <TextField
              label="Memory Request"
              size="small"
              placeholder="e.g. 128Mi"
              value={form.defaultMemoryRequest}
              onChange={handleChange('defaultMemoryRequest')}
            />
            <TextField
              label="Memory Limit"
              size="small"
              placeholder="e.g. 512Mi"
              value={form.defaultMemoryLimit}
              onChange={handleChange('defaultMemoryLimit')}
            />
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
}

export default AdminSettingsPage;
